<script setup lang="ts">
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

import { fullName, getPropertyTitle } from '@helpers/utils';
import { Dossier } from '@graphql/types/dossiers';
import { useRouter } from 'vue-router';
import { FinancingType } from '@graphql/enums/dossiers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

dayjs.locale('nl');
dayjs.extend(duration);
dayjs.extend(relativeTime);

export interface Props {
  dossier: Dossier;
  isRowActive: boolean;
  index: number;
}

interface Emits {
  (event: 'toggle:isRowActive'): void;
}

const emit = defineEmits<Emits>();
const props = defineProps<Props>();

const router = useRouter();

const statusTooltip = ref(null);
const isTooltipSet = ref(false);
const property = computed(() => props.dossier.properties[0]);

const goToRequest = () => {
  router.push(`/mijn-aanvragen/${props.dossier.id}`);
};

const specifications = [
  {
    icon: 'user',
    title: 'Klant',
    value: property.value.customer ? fullName(property.value.customer) : 'Niet bekend',
  },
  {
    icon: 'user-tie',
    title: 'Accountmanager',
    value: property.value.accountManager ? `${property.value.accountManager?.first_name} ${property.value.accountManager?.last_name}` : 'Niet bekend',
  },
  {
    icon: 'clock-rotate-left',
    title: 'Laatste wijziging',
    value: property.value.updated_at ? dayjs().to(property.value.updated_at) : null,
  },
  {
    icon: 'handshake',
    title: 'Passeerdatum',
    value: property.value.created_at ? formatDateDutch(property.value.created_at) : null,
  },
  {
    icon: 'calendar-clock',
    title: 'Datum afspraak',
    value: property.value.appointment_at ? `${formatDateDutch(property.value.appointment_at)} om ${dayjs(property.value.appointment_at).format('HH:mm')}` : null,
  },
];

const filteredSpecifications = computed(() => specifications.filter(spec => spec.value));

const showTooltip = () => {
  if (!isTooltipSet.value) {
    useTooltip({ ref: statusTooltip, content: property.value.advisor_status.tooltip ?? '' });
  }

  isTooltipSet.value = true;
};

</script>

<template>
  <div
    :data-test-id="`single-request-component-${index}`"
    class="gap-x-2 items-center base:col-span-6 base:flex base:flex-wrap md:grid md:col-span-4 md:grid-cols-5"
  >
    <div class="flex justify-center">
      <FontAwesomeIcon
        :icon="['fad', 'file']"
        :data-test-id="`intermediair-request-detail-icon-${props.index}`"
        class="text-2xl"
        @click="goToRequest()"
      />
    </div>
    <div class="flex flex-col md:!grid col-span-4 md:grid-cols-4">
      <div
        class="break-all hover:underline hover:cursor-pointer md:col-span-2"
        :data-test-id="`intermediair-single-request-id-${index.toString()}`"
        @click="goToRequest()"
      >
        {{ property.id }}
      </div>
      <div class="col-span-2 base:text-gray-700 md:text-inherit">
        {{ formatDateDutch(dossier.created_at) }}
      </div>
    </div>
  </div>
  <div class="base:row-start-2 base:py-4 base:col-span-12 md:col-span-3 md:py-0 md:row-start-auto flex flex-col">
    <div>
      {{ getPropertyTitle(property) }}
    </div>
    <span class="text-xs text-gray-700">
      {{ property.financing_type === FinancingType.BTL ? 'Verhuurhypotheek' : 'Ondernemershypotheek' }}
    </span>
  </div>
  <div
    class="base:row-start-3 base:col-span-12 base:text-blue-700 md:col-span-3 md:text-inherit md:row-start-auto flex justify-start items-center"
  >
    <div class="flex flex-col">
      <div>
        <span v-if="property.advisor_status && property.advisor_status.text" class="mr-2">{{ property.advisor_status.text }}</span>
        <FontAwesomeIcon ref="statusTooltip" :icon="['fas', 'info-circle']" size="1x" class="text-blue-300" @mouseover="showTooltip()" @click.stop="() => null" />
      </div>
      <span
        :class="property.advisor_status.needs_attention ? 'text-orange-700' : 'text-gray-700'"
        class="text-xs"
      >
        {{ property.advisor_status.needs_attention ? 'Actie vereist' : 'Geen actie vereist' }}
      </span>
    </div>
  </div>
  <div class="flex justify-end gap-4 base:col-span-6 md:col-span-2 md:pr-4">
    <FontAwesomeIcon
      :icon="['fad', 'magnifying-glass']"
      size="xl"
      class="cursor-pointer text-2xl"
      :data-test-id="`intermediair-request-detail-icon-${props.index}`"
      @click="goToRequest()"
    />
    <FontAwesomeIcon
      class="cursor-pointer text-2xl"
      :class="{ 'rotate-180': props.isRowActive }"
      :icon="['fas', 'chevron-down']"
      size="xl"
      :data-test-id="`intermediair-request-open-icon-${props.index}`"
      @click.stop="emit('toggle:isRowActive')"
    />
  </div>
  <UISlideDown :is-active="props.isRowActive" class="col-span-12 md:!mx-5" :class="{ 'mt-5': props.isRowActive }">
    <div
      class="grid border border-blue-300 rounded-lg p-5 gap-4 base:grid-cols-1 sm:grid-cols-2 md:!grid-cols-3 first:pl-3"
    >
      <UISpecification
        v-for="(specification, specificationIndex) in filteredSpecifications"
        :key="specificationIndex"
        :label="specification.title"
        :icon="specification.icon"
      >
        <span v-if="specification.value" class="text-blue-700 font-semibold text-sm">
          {{ specification.value }}
        </span>
      </UISpecification>
    </div>
  </UISlideDown>
</template>
